import type React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProtectedRoute from './ProtectedRoute';

type OnboardingStep = 'company' | 'subscription';

interface OnboardingRouteProps {
  children: React.ReactNode;
  step: OnboardingStep;
}

const OnboardingStateRoute: React.FC<OnboardingRouteProps> = ({
  children,
  step,
}) => {
  const { company } = useAuth();
  const [params] = useSearchParams();

  if (!company) {
    return step === 'company' ? (
      <>{children}</>
    ) : (
      <Navigate to="/onboarding/company" replace />
    );
  }

  const isReturningSubscriber =
    company.subscription?.hasSubscribed || !!company.subscription?.tier;
  if (!company.subscription?.isActive && isReturningSubscriber) {
    return (
      <Navigate
        to={{ pathname: '/subscription-expired', search: params.toString() }}
        replace
      />
    );
  }

  if (step === 'subscription' && params.has('cancel_checkout_id')) {
    return <>{children}</>;
  }

  if (!company.subscription?.tier) {
    return step === 'subscription' ? (
      <>{children}</>
    ) : (
      <Navigate to="/onboarding/subscription" replace />
    );
  }

  return (
    <Navigate
      to={company.subscription.isActive ? '/' : '/subscription-expired'}
      replace
    />
  );
};

const OnboardingRoute: React.FC<OnboardingRouteProps> = ({
  children,
  step,
}) => (
  <ProtectedRoute requiresCompany={false} requiresSubscription={false}>
    <OnboardingStateRoute step={step}>{children}</OnboardingStateRoute>
  </ProtectedRoute>
);

export default OnboardingRoute;
